import {DispatchEntity, dispatchRequestStatus, dispatchStatus} from "@ots-frontend/ots-data";
import type {DataGridColumnDefinition} from "../../DataGrid/useDataGridColumnLayout";
import {UseEntityDataGridProps} from "../../EntityDataGrid/useEntityDataGrid";
import {useTranslation} from "react-i18next";
import {useMemo, useRef} from "react";
import {GridCellKind} from "@glideapps/glide-data-grid";
import {getFormattedDateTime} from "../../../date/FormattedDateTime/FormattedDateTime";
import {ImageTextCell} from "../../DataGrid/cells/ImageTextCell";

export type UseDispatchDataGridColsProps = Pick<UseEntityDataGridProps<DispatchEntity>, "colWidths">;

export type UseDispatchDataGridResult = {
  colsMap: DataGridColumnDefinition<DispatchEntity>[];
};

export const LEGACY_DISPATCH_COLUMN_IDS = [
  "INDICATORS",
  "SCHEDULED",
  "PERFORM_ORDER",
  "DRIVER",
  "REQUEST",
  "VEHICLE",
  "PICKUP_COMPANY",
  "PICKUP_SITE",
  "PICKUP_CONTAINER",
  "DELIVERY_COMPANY",
  "DELIVERY_SITE",
  "DELIVERY_CONTAINER",
  "QUANTITY",
  "PICKUP_SERVICE",
  "FREIGHT_TYPE",
  "STATUS",
] as const;
/** Positional compatibility only; managed grid edits resolve PERFORM_ORDER by ID. */
export const DISPATCH_DATA_GRID_PERFORM_ORDER_COL = 2;

/** Whether the carryover job has moved this dispatch to a later day at least once. */
export const isCarriedOver = (d: DispatchEntity | undefined): d is DispatchEntity & {carryoverCount: number} =>
  (d?.carryoverCount ?? 0) > 0;

export function useDispatchDataGridCols(props: UseDispatchDataGridColsProps): UseDispatchDataGridResult {
  const widths = useRef(props.colWidths);
  const colWidths = widths.current;

  const {t} = useTranslation(["common"]);

  const colsMap: DataGridColumnDefinition<DispatchEntity>[] = useMemo(() => {
    let index = 0;
    const columns: DataGridColumnDefinition<DispatchEntity>[] = [
      {
        id: "INDICATORS",
        sortable: false,
        columnLabel: "Indicators",
        title: "",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        getContent: (d) => {
          const data: string[] = [];

          const hasMessages = typeof d?.message !== "undefined";
          if (hasMessages) {
            data.push(import.meta.env.BASE_URL + "message.svg");
          }

          if (d?.splitGroupId) {
            data.push(import.meta.env.BASE_URL + "arrows-split.svg");
          }

          if (d?.notified) {
            data.push(import.meta.env.BASE_URL + "bell-ringing.svg");
          }

          if (isCarriedOver(d)) {
            data.push(import.meta.env.BASE_URL + "calendar-repeat.svg");
          }

          return {
            kind: GridCellKind.Image,
            data: data,
            allowOverlay: false,
            allowAdd: false,
            cursor: hasMessages ? "pointer" : undefined,
          };
        },
      },
      {
        id: "SCHEDULED",
        sortable: true,
        getSortValue: (d) => d.scheduleDttm,
        title: "Scheduled",
        // wide enough for the time plus a carryover count
        width: colWidths?.at(index++) ?? 160,
        hasMenu: true,
        getContent: (d) => {
          const scheduled = d?.scheduleDttm != null ? getFormattedDateTime(d.scheduleDttm) : "";
          return {
            kind: GridCellKind.Text,
            data: scheduled,
            allowOverlay: false,
            // an em space reads as a tab; canvas text drops real tabs
            displayData: isCarriedOver(d) ? `${scheduled}\u2003↻${d.carryoverCount}` : scheduled,
          };
        },
      },
      {
        id: "PERFORM_ORDER",
        sortable: true,
        getSortValue: (d) => d.performOrder,
        title: "Perform Order",
        width: colWidths?.at(index++) ?? 120,
        hasMenu: true,
        getContent: (d) => ({
          kind: GridCellKind.Number,
          data: d?.performOrder,
          allowOverlay: true,
          displayData: d?.performOrder?.toString() ?? "",
        }),
      },
      {
        id: "DRIVER",
        sortable: true,
        getSortValue: (d) => d.personName,
        title: "Driver",
        width: colWidths?.at(index++) ?? 150,
        hasMenu: true,
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.personName ?? "",
          allowOverlay: false,
          displayData: d?.personName ?? "",
        }),
      },
      {
        id: "REQUEST",
        sortable: true,
        getSortValue: (d) => d.workOrderNumber ?? d.dispatchRequest?.displayName,
        title: "Request",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        getContent: (d) => {
          let text = d?.workOrderNumber ?? "";
          let image: string | undefined;

          const dispatchRequest = d?.dispatchRequest;
          if (dispatchRequest) {
            text += " (";

            const canceled =
              dispatchRequest.status === dispatchRequestStatus.pendingCancel ||
              dispatchRequest.status === dispatchRequestStatus.canceled;

            if (canceled) {
              text += t<string>(
                dispatchRequestStatus.canceled.displayValue.id,
                dispatchRequestStatus.canceled.displayValue.defaultMessage
              );
              text += "";
              image = import.meta.env.BASE_URL + "alert-triangle-red.svg";
            }

            text += dispatchRequest.displayName;
            if (dispatchRequest.externalId && dispatchRequest.externalSystem) {
              text += " - Ext. ";
              text += dispatchRequest.externalSystem;
              if (dispatchRequest.externalId !== d.workOrderNumber) {
                text += ": ";
                text += dispatchRequest.externalId;
              }
            }
            text += ")";
          }

          if (!text) {
            text = "";
          }

          return {
            kind: GridCellKind.Custom,
            allowOverlay: false,
            copyData: text,
            data: {
              kind: "image-text-cell",
              name: text,
              image: image,
            },
          } as ImageTextCell;
        },
      },
      {
        id: "VEHICLE",
        sortable: true,
        getSortValue: (d) => d.vehicleName,
        title: "Vehicle",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d.vehicleName ?? "",
          allowOverlay: false,
          displayData: d.vehicleName ?? "",
        }),
      },
      {
        id: "PICKUP_COMPANY",
        sortable: true,
        getSortValue: (d) => d.pickupSiteCompanyName,
        title: "P/U Company",
        width: colWidths?.at(index++) ?? 250,
        hasMenu: true,
        group: "Pickup",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.pickupSiteCompanyName ?? "",
          allowOverlay: false,
          displayData: d?.pickupSiteCompanyName ?? "",
        }),
      },
      {
        id: "PICKUP_SITE",
        sortable: true,
        getSortValue: (d) => d.pickupSiteName,
        title: "P/U Site",
        width: colWidths?.at(index++) ?? 250,
        hasMenu: true,
        group: "Pickup",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.pickupSiteName ?? "",
          allowOverlay: false,
          displayData: d?.pickupSiteName ?? "",
        }),
      },
      {
        id: "PICKUP_CONTAINER",
        sortable: true,
        getSortValue: (d) => d.pickupContainerName,
        title: "P/U Container",
        width: colWidths?.at(index++) ?? 250,
        hasMenu: true,
        group: "Pickup",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.pickupContainerName ?? "",
          allowOverlay: false,
          displayData: d?.pickupContainerName ?? "",
        }),
      },
      {
        id: "DELIVERY_COMPANY",
        sortable: true,
        getSortValue: (d) => d.deliverySiteCompanyName,
        title: "Del. Company",
        width: colWidths?.at(index++) ?? 250,
        hasMenu: true,
        group: "Delivery",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.deliverySiteCompanyName ?? "",
          allowOverlay: false,
          displayData: d?.deliverySiteCompanyName ?? "",
        }),
      },
      {
        id: "DELIVERY_SITE",
        sortable: true,
        getSortValue: (d) => d.deliverySiteName,
        title: "Del. Site",
        width: colWidths?.at(index++) ?? 250,
        hasMenu: true,
        group: "Delivery",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.deliverySiteName ?? "",
          allowOverlay: false,
          displayData: d?.deliverySiteName ?? "",
        }),
      },
      {
        id: "DELIVERY_CONTAINER",
        sortable: true,
        getSortValue: (d) => d.deliveryContainerName,
        title: "Del. Container",
        width: colWidths?.at(index++) ?? 250,
        hasMenu: true,
        group: "Delivery",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.deliveryContainerName ?? "",
          allowOverlay: false,
          displayData: d?.deliveryContainerName ?? "",
        }),
      },
      {
        id: "QUANTITY",
        sortable: true,
        getSortValue: (d) => d.quantity,
        title: "Qty",
        hasMenu: true,
        width: colWidths?.at(index++) ?? 100,
        group: "Load",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d.quantity == null ? "" : `${d.quantity}${d.quantityUom ? ` ${d.quantityUom}` : ""}`,
          allowOverlay: false,
          displayData: d.quantity == null ? "" : `${d.quantity}${d.quantityUom ? ` ${d.quantityUom}` : ""}`,
        }),
      },
      {
        id: "PICKUP_SERVICE",
        sortable: true,
        getSortValue: (d) => d.pickupServiceTypeName,
        title: "P/U Service Type",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        group: "Load",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.pickupServiceTypeName ?? "",
          allowOverlay: false,
          displayData: d?.pickupServiceTypeName ?? "",
        }),
      },
      {
        id: "FREIGHT_TYPE",
        sortable: true,
        getSortValue: (d) => d.freightTypeName,
        title: "Freight Type",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        group: "Load",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.freightTypeName ?? "",
          allowOverlay: false,
          displayData: d?.freightTypeName ?? "",
        }),
      },
      {
        id: "STATUS",
        sortable: true,
        getSortValue: (d) => d.status?.code,
        title: "Status",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        getContent: (d) => {
          let display = d?.status?.code ?? "";
          if (d.status === dispatchStatus.started) {
            if (d.pickupTransportId) {
              display = "Picked-Up";
            }
          }

          return {
            kind: GridCellKind.Text,
            data: d?.status?.code ?? "",
            allowOverlay: false,
            displayData: display,
          };
        },
      },
      {
        id: "ORIGINAL_SCHEDULED",
        title: "Original scheduled",
        width: 150,
        defaultVisible: false,
        sortable: true,
        hasMenu: true,
        getSortValue: (d) => d.originalScheduleDttm,
        getContent: (d) => {
          const text = d.originalScheduleDttm != null ? getFormattedDateTime(d.originalScheduleDttm) : "";
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "ID",
        title: "Dispatch ID",
        width: 150,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: undefined,
        getSortValue: (d) => d.id,
        getContent: (d) => {
          const value = d.id;
          const text = value == null ? "" : String(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "CATEGORY",
        title: "Category",
        width: 90,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: undefined,
        getSortValue: (d) => d.category?.code,
        getContent: (d) => {
          const value = d.category?.code;
          const text = value == null ? "" : String(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "INTERNAL_WORK_ORDER",
        title: "Internal work order",
        width: 150,
        defaultVisible: false,
        sortable: true,
        hasMenu: true,
        getSortValue: (d) => d.internalWorkOrderNumber,
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d.internalWorkOrderNumber ?? "",
          displayData: d.internalWorkOrderNumber ?? "",
          allowOverlay: false,
        }),
      },
      {
        id: "TICKET_NUMBER",
        title: "Ticket number",
        width: 150,
        defaultVisible: false,
        sortable: true,
        hasMenu: true,
        getSortValue: (d) => d.ticketNumber,
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d.ticketNumber ?? "",
          displayData: d.ticketNumber ?? "",
          allowOverlay: false,
        }),
      },
      {
        id: "TRAILER",
        title: "Trailer",
        width: 120,
        defaultVisible: true,
        hasMenu: true,
        sortable: true,
        group: undefined,
        getSortValue: (d) => d.trailerName,
        getContent: (d) => {
          const value = d.trailerName;
          const text = value == null ? "" : String(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "VEHICLE_AND_TRAILER",
        title: "Vehicle / Trailer",
        width: 200,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        getSortValue: (d) => `${d.vehicleName ?? "-"} / ${d.trailerName ?? "-"}`,
        getContent: (d) => {
          const text = `${d.vehicleName ?? "-"} / ${d.trailerName ?? "-"}`;
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "TRUCK_COMPANY",
        title: "Provider",
        width: 180,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: undefined,
        getSortValue: (d) => d.personCompanyName,
        getContent: (d) => {
          const value = d.personCompanyName;
          const text = value == null ? "" : String(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "PICKUP_WELL",
        title: "P/U Well",
        width: 180,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: "Pickup",
        getSortValue: (d) => d.pickupWellName,
        getContent: (d) => {
          const value = d.pickupWellName;
          const text = value == null ? "" : String(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "DELIVERY_WELL",
        title: "Del. Well",
        width: 180,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: "Delivery",
        getSortValue: (d) => d.deliveryWellName,
        getContent: (d) => {
          const value = d.deliveryWellName;
          const text = value == null ? "" : String(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "DELIVERY_SERVICE",
        title: "Del. Service Type",
        width: 160,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: "Delivery",
        getSortValue: (d) => d.deliveryServiceTypeName,
        getContent: (d) => {
          const value = d.deliveryServiceTypeName;
          const text = value == null ? "" : String(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "UOM",
        title: "UOM",
        width: 80,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: "Load",
        getSortValue: (d) => d.quantityUom,
        getContent: (d) => {
          const value = d.quantityUom;
          const text = value == null ? "" : String(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "AUTHORIZED_BY",
        title: "Authorized by",
        width: 150,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: "Audit",
        getSortValue: (d) => d.authorizeName,
        getContent: (d) => {
          const value = d.authorizeName;
          const text = value == null ? "" : String(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "CREATED_BY",
        title: "Created by",
        width: 150,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: "Audit",
        getSortValue: (d) => d.createByName,
        getContent: (d) => {
          const value = d.createByName;
          const text = value == null ? "" : String(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "CREATED",
        title: "Created",
        width: 150,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: "Audit",
        getSortValue: (d) => d.createDttm,
        getContent: (d) => {
          const value = d.createDttm;
          const text = value == null ? "" : getFormattedDateTime(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "UPDATED",
        title: "Last modified",
        width: 150,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: "Audit",
        getSortValue: (d) => d.lastModifiedDttm,
        getContent: (d) => {
          const value = d.lastModifiedDttm;
          const text = value == null ? "" : getFormattedDateTime(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "ACKNOWLEDGED",
        title: "Driver acknowledged",
        width: 150,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: "Audit",
        getSortValue: (d) => d.personAckDttm,
        getContent: (d) => {
          const value = d.personAckDttm;
          const text = value == null ? "" : getFormattedDateTime(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "NOTIFIED",
        title: "Notified",
        width: 90,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: undefined,
        getSortValue: (d) => d.notified,
        getContent: (d) => {
          const value = d.notified;
          const text = value == null ? "" : String(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
      {
        id: "SPLIT_GROUP",
        title: "Split group",
        width: 140,
        defaultVisible: false,
        hasMenu: true,
        sortable: true,
        group: undefined,
        getSortValue: (d) => d.splitGroupId,
        getContent: (d) => {
          const value = d.splitGroupId;
          const text = value == null ? "" : String(value);
          return {kind: GridCellKind.Text, data: text, displayData: text, allowOverlay: false};
        },
      },
    ];
    const trailer = columns.find((column) => column.id === "TRAILER");
    const ordered = columns.filter((column) => column.id !== "TRAILER");
    if (trailer) ordered.splice(ordered.findIndex((column) => column.id === "VEHICLE") + 1, 0, trailer);
    return ordered;
  }, [colWidths, t]);

  return {
    colsMap: colsMap,
  };
}
